import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Landmark, TrendingUp, Users, Wallet, FileBarChart, RefreshCw } from 'lucide-react';
import { fmtMoney } from './money';
import ReportDocument from './ui/ReportRenderer';
import { catalog, isNonprofit, VIEWER_REPORT_KEYS } from './reportCatalog';

const OLFIN = `${process.env.REACT_APP_BACKEND_URL}/api/olfin`;
const ORG_API = `${process.env.REACT_APP_BACKEND_URL}/api/organizations`;

function ProfileSummary({ orgId, isDark }) {
  // Read-only mirror in Finance — the editable form lives on the AD Organization page.
  const [p, setP] = useState(null);
  useEffect(() => {
    if (!orgId) return;
    axios.get(`${ORG_API}/${orgId}/finance-profile`, { withCredentials: true })
      .then((r) => setP(r.data || {})).catch(() => setP({}));
  }, [orgId]);
  if (!p) return null;
  const la = p.legal_address || {};
  const addr = [la.address1, [la.city, la.state, la.postal_code].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const lab = `text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`;
  return (
    <div data-testid="finance-profile-summary"
         className={`rounded-xl border p-4 ${isDark ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200'}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold">Company profile</span>
        <a href="/dashboard?tab=organizations" data-testid="fp-edit-link"
           className="text-xs text-emerald-500 hover:underline">Edit on the Organization page →</a>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div><div className={lab}>Legal name</div><div>{p.legal_name || '—'}</div></div>
        <div><div className={lab}>EIN</div><div>{p.ein || '—'}</div></div>
        <div><div className={lab}>Business type</div><div>{p.business_type || '—'}</div></div>
        <div><div className={lab}>Legal address</div><div>{addr || '—'}</div></div>
      </div>
    </div>
  );
}


const Card = ({ icon: Icon, label, value, testid, isDark }) => (
  <div data-testid={testid}
       className={`rounded-xl p-4 border ${isDark ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200'}`}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className="w-4 h-4 text-emerald-500" />
      <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{label}</span>
    </div>
    <div className={`text-2xl font-semibold ${isDark ? 'text-slate-100' : 'text-slate-800'}`}>{value}</div>
  </div>
);

export default function FinanceViewer({ orgId, isDark = false, title = 'Finance — Report Viewer', canEdit = false }) {
  const [companies, setCompanies] = useState([]);
  const [cid, setCid] = useState(null);
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [rep, setRep] = useState(null);
  const [repDef, setRepDef] = useState(null);
  const [activity, setActivity] = useState([]);

  const base = cid ? `${OLFIN}/companies/${cid}` : null;

  useEffect(() => {
    let dead = false;
    (async () => {
      setLoading(true); setErr('');
      try {
        const r = await axios.get(`${OLFIN}/companies`, { params: orgId ? { org_id: orgId } : {}, withCredentials: true });
        if (dead) return;
        const list = r.data || [];
        setCompanies(list);
        const linked = list.find((c) => c.is_linked) || list[0];
        setCid(linked ? linked.company_id : null);
        if (!linked) setLoading(false);
      } catch (e) {
        if (!dead) { setErr('No finance company is available for your organization yet.'); setLoading(false); }
      }
    })();
    return () => { dead = true; };
  }, [orgId]);

  const loadDash = useCallback(async () => {
    if (!base) return;
    setLoading(true); setErr('');
    try {
      const d = (await axios.get(`${base}/ad-dashboard`, { withCredentials: true })).data;
      setDash(d);
    } catch (e) { setErr('Could not load the finance snapshot.'); }
    setLoading(false);
  }, [base]);

  useEffect(() => { if (base) loadDash(); }, [base, loadDash]);

  useEffect(() => {
    if (!orgId) return;
    axios.get(`${OLFIN}/ol-activity`, { params: { org_id: orgId }, withCredentials: true })
      .then((r) => setActivity(r.data?.rows || [])).catch(() => setActivity([]));
  }, [orgId, dash]);

  const runReport = async (def) => {
    if (!base) return;
    setRepDef(def); setRep(null);
    const params = { preset: 'this_fiscal_year_to_date', ...(def.params || {}) };
    try {
      const d = (await axios.post(`${base}/reports/run`, { report: def.k, params }, { withCredentials: true })).data;
      setRep(d);
    } catch (e) { setRep({ error: 'Report failed to load.' }); }
  };

  const company = companies.find((c) => c.company_id === cid);
  const reports = catalog(isNonprofit(company)).filter((d) => VIEWER_REPORT_KEYS.includes(d.k));
  const ni = dash?.net_income;
  const niVal = Array.isArray(ni) ? ni[0] : ni;

  return (
    <div data-testid="finance-viewer" className={`space-y-5 ${isDark ? 'text-slate-100' : 'text-slate-800'}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Landmark className="w-5 h-5 text-emerald-500" />
          <h2 className="text-lg font-semibold">{title}</h2>
        </div>
        <div className="flex items-center gap-2">
          {companies.length > 1 && (
            <select data-testid="fv-company-picker" value={cid || ''} onChange={(e) => setCid(e.target.value)}
                    className={`text-sm rounded-md px-2 py-1.5 border ${isDark ? 'bg-slate-800 border-slate-600' : 'bg-white border-slate-300'}`}>
              {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
            </select>
          )}
          <button data-testid="fv-refresh" onClick={loadDash}
                  className="text-sm inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-500">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {err && <div data-testid="fv-error" className="text-sm text-amber-500">{err}</div>}
      {loading && <div data-testid="fv-loading" className="text-sm opacity-60">Loading…</div>}

      {canEdit && orgId && <ProfileSummary orgId={orgId} isDark={isDark} />}

      {activity.length > 0 && (
        <div data-testid="fv-sync-activity" className={`rounded-xl border p-3 ${isDark ? 'bg-amber-900/20 border-amber-800' : 'bg-amber-50 border-amber-200'}`}>
          <div className="text-sm font-semibold text-amber-600 mb-1">Finance sync needs attention ({activity.length})</div>
          <ul className="text-xs space-y-1">
            {activity.slice(0, 8).map((a, i) => (
              <li key={i} className="flex justify-between gap-2">
                <span>{a.type} — <span className="opacity-70">{a.status}</span></span>
                <span className="opacity-60 truncate max-w-[50%]">{a.detail}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!loading && dash && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card icon={TrendingUp} label="Net income (FY to date)" value={fmtMoney(niVal || 0)} testid="fv-net-income" isDark={isDark} />
            <Card icon={Wallet} label="Open A/R (owed to you)" value={fmtMoney(dash.ar_total || 0)} testid="fv-ar" isDark={isDark} />
            <Card icon={Wallet} label="Open A/P (you owe)" value={fmtMoney(dash.ap_total || 0)} testid="fv-ap" isDark={isDark} />
            <Card icon={Users} label="Billed families" value={dash.family_balances?.count ?? 0} testid="fv-families" isDark={isDark} />
          </div>

          <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200'}`}>
            <div className={`px-4 py-2 text-sm font-semibold border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>Bank &amp; card balances</div>
            <table className="w-full text-sm" data-testid="fv-banks">
              <tbody>
                {(dash.banks || []).length === 0 && <tr><td className="px-4 py-3 opacity-60">No bank or credit-card accounts.</td></tr>}
                {(dash.banks || []).map((b, i) => (
                  <tr key={i} className={`border-t ${isDark ? 'border-slate-700' : 'border-slate-100'}`}>
                    <td className="px-4 py-2">{b.name} <span className="opacity-50 text-xs">({b.type})</span></td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmtMoney(b.balance || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200'}`}>
            <div className={`px-4 py-2 text-sm font-semibold border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>Family balances</div>
            <table className="w-full text-sm" data-testid="fv-family-balances">
              <thead><tr className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}><th className="px-4 py-1.5 text-left">Family</th><th className="px-4 py-1.5 text-left">Email</th><th className="px-4 py-1.5 text-right">Balance</th></tr></thead>
              <tbody>
                {(dash.family_balances?.rows || []).length === 0 && <tr><td colSpan={3} className="px-4 py-3 opacity-60">No OL-linked families billed yet.</td></tr>}
                {(dash.family_balances?.rows || []).map((f, i) => (
                  <tr key={i} className={`border-t ${isDark ? 'border-slate-700' : 'border-slate-100'}`}>
                    <td className="px-4 py-2">{f.name}</td>
                    <td className="px-4 py-2 opacity-70">{f.email}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmtMoney(f.balance || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold inline-flex items-center gap-1"><FileBarChart className="w-4 h-4 text-emerald-500" /> Reports:</span>
            {reports.map((d) => (
              <button key={d.id} data-testid={`fv-report-${d.k}`} onClick={() => runReport(d)}
                      className={`text-xs px-3 py-1.5 rounded-full border ${repDef?.k === d.k ? 'bg-emerald-600 text-white border-emerald-600' : (isDark ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-100')}`}>
                {d.label}
              </button>
            ))}
          </div>

          {rep && (
            <div data-testid="fv-report-doc" className={`rounded-xl border p-4 ${isDark ? 'bg-slate-900/60 border-slate-700' : 'bg-white border-slate-200'}`}>
              {rep.error ? <div className="text-sm text-amber-500">{rep.error}</div>
                : <ReportDocument rep={rep} title={repDef?.label} company={company?.name} basis="accrual" />}
            </div>
          )}
        </>
      )}
    </div>
  );
}
